import { useState, useCallback } from 'react';
import { IShare } from '../../types';
import { get } from 'lodash';

// check if weight has a decimal point
// validation is post multiplying by 100, hence the check for 0
export const hasDecimal = (value = 0) => {
  let decimalLength = 0;

  if (value && Math.floor(value) !== value) {
    decimalLength = get(value.toString().split('.'), [1], '').length;
  }
  return decimalLength > 0;
};

function useShares(
  inputShares: IShare[],
): [IShare[], string | null, (sid: number, s: IShare) => void, () => void, (sid: number) => void] {
  const [error, setError] = useState<string | null>(null);
  const [shares, setInternalShares] = useState(inputShares);

  const setShares = useCallback((ss: IShare[]) => {
    setInternalShares(ss);
    const total = ss.reduce((a, b) => ({ weight: a.weight + b.weight }), { weight: 0 }).weight;

    const err: string[] = [];
    if (ss.some((s) => s.weight <= 0)) {
      err.push('Weight must be greater than 0.');
    }

    if (total !== 10000) {
      err.push('Total weight must be 100.');
    }

    if (ss.some((s) => s.resources.length === 0)) {
      err.push('Please select resources.');
    }

    if (ss.some((s) => hasDecimal(s.weight))) {
      err.push('Only 2 decimal places are allowed.');
    }

    setError(err.join(' ') || null);
  }, []);

  const setShare = useCallback(
    (sid: number, s: IShare) => {
      const share = shares.filter((s) => s.id === sid)[0];
      share.weight = s.weight;
      share.resources = s.resources;
      setShares(JSON.parse(JSON.stringify(shares)));
    },
    [shares],
  );

  const addShare = useCallback(() => {
    const ss = JSON.parse(JSON.stringify(shares));
    ss.push({ id: new Date().getTime(), weight: 0, resources: [] });
    setShares(ss);
  }, [shares]);

  const deleteShare = useCallback(
    (sid: number) => {
      const ss = shares.filter((s) => s.id !== sid);
      setShares(ss);
    },
    [shares],
  );

  return [shares, error, setShare, addShare, deleteShare];
}

export default useShares;
